<template>
  <div class="app-container">
    <Header />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="remote-outdoor-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>

          <section class="blog-section">
            <p>
              Remote outdoor classes blend live drone technology with the
              natural world, creating immersive experiences that deepen
              students' understanding of global environments and cultures. By
              streaming real-time aerial footage of remote landscapes, educators
              can extend the classroom beyond school walls, foster empathy for
              diverse ecosystems, and build essential spatial and cultural
              literacy. This approach leverages best practices from virtual
              field trips and outdoor education to engage learners and align
              with curriculum objectives, all while promoting global awareness
              and stewardship.
            </p>
          </section>

          <section class="blog-section">
            <h2>Expanding Horizons with Remote Outdoor Classes</h2>
            <p>
              Remote Outdoor Classes allow students to "visit" distant
              locations—coral reefs, mountain ranges, or cultural heritage
              sites—without leaving the classroom
              <a
                href="https://www.blog.ryco.io/post/fostering-global-awareness-through-virtual-field-trips"
                target="_blank"
                class="blog-link"
                >as noted by education experts</a
              >. Research shows that when educators prepare students with
              pre-trip activities and integrate background research, virtual
              trips significantly enhance engagement and retention
              <a
                href="https://virtualfieldtrips.org/wp-content/uploads/2018/08/Research-Supporting-the-Many-Uses-for-Virtual-Field-Trips.pdf"
                target="_blank"
                class="blog-link"
                >according to comprehensive research studies</a
              >. Live drone tours take this a step further by offering real-time
              interaction, enabling learners to guide the flight path and ask
              questions as events unfold
              <a
                href="https://www.researchgate.net/publication/237405940_International_Virtual_Field_Trips_a_New_Direction"
                target="_blank"
                class="blog-link"
                >as confirmed by field experts</a
              >.
            </p>
          </section>

          <section class="blog-section">
            <h2>Engaging Students Through Outdoor Contexts</h2>
            <p>
              Outdoor learning boosts student engagement, wellbeing, and
              ownership of learning when lessons take place in natural settings
              <a
                href="https://www.edutopia.org/article/learning-blooms-outdoor-classrooms/"
                target="_blank"
                class="blog-link"
                >as Edutopia reports</a
              >. Studies report that outdoor classrooms improve social skills,
              collaboration, and self-concept factors—benefits that carry over
              when nature is streamed live into the school environment
              <a
                href="https://pmc.ncbi.nlm.nih.gov/articles/PMC9149177/"
                target="_blank"
                class="blog-link"
                >according to recent research</a
              >. Combining live drone footage with on-site observations allows
              students to compare local outdoor sites with global examples,
              reinforcing their understanding of ecological processes.
            </p>
          </section>

          <section class="blog-section">
            <h2>Cultivating Cultural Empathy and Global Citizenship</h2>
            <p>
              Building global awareness requires more than geography facts; it
              demands cultural empathy. Remote classes that include live
              exchanges with pilots or experts in other countries help students
              appreciate different ways of life and environmental challenges
              <a
                href="https://www.blog.ryco.io/post/fostering-global-awareness-through-virtual-field-trips"
                target="_blank"
                class="blog-link"
                >as educators have noted</a
              >. One study found that even a single remote interaction between
              international cohorts increases cultural curiosity and enriches
              academic projects, laying the groundwork for global citizenship
              <a
                href="https://www.tandfonline.com/doi/full/10.1080/00131881.2023.2285762"
                target="_blank"
                class="blog-link"
                >as researchers have demonstrated</a
              >.
            </p>
          </section>

          <section class="blog-section">
            <h2>Integrating Real-Time Technology</h2>
            <p>
              Immersive technologies, such as live drone streaming, amplify the
              benefits of remote outdoor classes by providing synchronous,
              high-definition views of landscapes and cultural sites
              <a
                href="https://bmcmededuc.biomedcentral.com/articles/10.1186/s12909-023-04333-x"
                target="_blank"
                class="blog-link"
                >according to educational technology research</a
              >. Unlike pre-recorded video, live feeds maintain student
              attention and allow for adaptive teaching—pilots can adjust camera
              angles at students' request and educators can pivot based on
              real-time observations
              <a
                href="https://www.pearson.com/international-schools/international-schools-blog/2024/10/the-long-term-effects-of-remote-learning-on-students.html"
                target="_blank"
                class="blog-link"
                >as noted in recent analysis</a
              >. This interactivity aligns with multimedia learning principles,
              enhancing presence, enjoyment, and retention.
            </p>
          </section>

          <section class="blog-section">
            <h2>Aligning with Curriculum Objectives</h2>
            <p>
              Educators need clear connections between innovative activities and
              learning standards. Each remote outdoor class can be mapped to
              national frameworks—NGSS performance expectations, state geography
              standards, or IB criteria—ensuring that key concepts like plate
              tectonics, biomes, or human–environment interaction are explicitly
              addressed
              <a
                href="https://virtualfieldtrips.org/wp-content/uploads/2018/08/Research-Supporting-the-Many-Uses-for-Virtual-Field-Trips.pdf"
                target="_blank"
                class="blog-link"
                >as shown in implementation guides</a
              >. Detailed teacher guides accompany live tours, providing
              discussion prompts and assessment rubrics that streamline lesson
              planning and evaluation
              <a
                href="https://www.tandfonline.com/doi/full/10.1080/00131881.2023.2285762"
                target="_blank"
                class="blog-link"
                >as curriculum experts have outlined</a
              >.
            </p>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>Practical Implementation</h2>
            <p>
              Implementing remote outdoor classes requires minimal logistics: a
              reliable internet connection, a display device, and scheduling
              with a drone pilot. Schools can integrate sessions into existing
              units on ecosystems, climate change, or cultural geography, using
              live footage to illustrate concepts and spark inquiry. A
              systematic review of outdoor learning highlights that simple
              setups—under a few trees or via streamed content—can transform
              instructional practices and drive student motivation
              <a
                href="https://www.edutopia.org/article/learning-blooms-outdoor-classrooms/"
                target="_blank"
                class="blog-link"
                >as educators have reported</a
              >.
            </p>

            <h2>Conclusion</h2>
            <p>
              Remote outdoor classes harness live drone technology and outdoor
              education research to build global awareness, engagement, and
              empathy. By bringing real-time views of distant landscapes and
              cultures into the classroom, educators empower students to become
              informed, connected global citizens. For more information on
              scheduling a remote outdoor class with Skyhost, visit
              <a
                href="https://skyhost.ai/schools"
                target="_blank"
                class="blog-link"
                >skyhost.ai/schools</a
              >.
            </p>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "RemoteOutdoorBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "remote-outdoor-classes";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .filter((blog) => blog.tags.some((tag) => this.tags.includes(tag)))
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.remote-outdoor-blog {
  font-family: "Inter", "Helvetica", sans-serif;
  line-height: 1.8;
  color: #333;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 500px;
  border-radius: 8px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
